<template>
  <div class="ds-page">
    <div class="ds-header">
      <h1 class="ds-title">Design System</h1>
      <p class="ds-subtitle">CSS custom properties and design tokens</p>
    </div>

    <!-- Colors -->
    <section class="ds-section">
      <h2 class="ds-section-title">Colors</h2>

      <div class="ds-group">
        <h3 class="ds-group-title">Backgrounds</h3>
        <div class="ds-swatches">
          <div class="ds-swatch" style="background: var(--bg-primary)">
            <span class="ds-swatch-name">--bg-primary</span>
          </div>
          <div class="ds-swatch" style="background: var(--bg-secondary)">
            <span class="ds-swatch-name">--bg-secondary</span>
          </div>
          <div class="ds-swatch" style="background: var(--bg-tertiary)">
            <span class="ds-swatch-name">--bg-tertiary</span>
          </div>
          <div class="ds-swatch" style="background: var(--bg-game)">
            <span class="ds-swatch-name">--bg-game</span>
          </div>
          <div class="ds-swatch" style="background: var(--bg-sidebar)">
            <span class="ds-swatch-name">--bg-sidebar</span>
          </div>
          <div class="ds-swatch" style="background: var(--bg-header)">
            <span class="ds-swatch-name">--bg-header</span>
          </div>
          <div class="ds-swatch" style="background: var(--bg-hover)">
            <span class="ds-swatch-name">--bg-hover</span>
          </div>
        </div>
      </div>

      <div class="ds-group">
        <h3 class="ds-group-title">Accent Colors</h3>
        <div class="ds-swatches">
          <div class="ds-swatch" style="background: var(--accent-primary)">
            <span class="ds-swatch-name">--accent-primary</span>
          </div>
          <div class="ds-swatch" style="background: var(--accent-primary-hover)">
            <span class="ds-swatch-name">--accent-primary-hover</span>
          </div>
          <div class="ds-swatch" style="background: var(--accent-secondary)">
            <span class="ds-swatch-name">--accent-secondary</span>
          </div>
          <div class="ds-swatch" style="background: var(--accent-secondary-hover)">
            <span class="ds-swatch-name">--accent-secondary-hover</span>
          </div>
          <div class="ds-swatch" style="background: var(--accent-gold)">
            <span class="ds-swatch-name">--accent-gold</span>
          </div>
          <div class="ds-swatch" style="background: var(--accent-purple)">
            <span class="ds-swatch-name">--accent-purple</span>
          </div>
        </div>
      </div>

      <div class="ds-group">
        <h3 class="ds-group-title">State Colors</h3>
        <div class="ds-swatches">
          <div class="ds-swatch" style="background: var(--color-success)">
            <span class="ds-swatch-name">--color-success</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-success-mid)">
            <span class="ds-swatch-name">--color-success-mid</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-success-light)">
            <span class="ds-swatch-name">--color-success-light</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-success-faint)">
            <span class="ds-swatch-name">--color-success-faint</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-danger)">
            <span class="ds-swatch-name">--color-danger</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-danger-light)">
            <span class="ds-swatch-name">--color-danger-light</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-danger-faint)">
            <span class="ds-swatch-name">--color-danger-faint</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-warning)">
            <span class="ds-swatch-name">--color-warning</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-warning-light)">
            <span class="ds-swatch-name">--color-warning-light</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-info)">
            <span class="ds-swatch-name">--color-info</span>
          </div>
          <div class="ds-swatch" style="background: var(--color-info-light)">
            <span class="ds-swatch-name">--color-info-light</span>
          </div>
          <div class="ds-swatch ds-swatch-light" style="background: var(--color-muted)">
            <span class="ds-swatch-name">--color-muted</span>
          </div>
        </div>
      </div>

      <div class="ds-group">
        <h3 class="ds-group-title">Text Colors</h3>
        <div class="ds-swatches">
          <div class="ds-swatch" style="background: var(--text-primary)">
            <span class="ds-swatch-name" style="color: var(--bg-primary)">--text-primary</span>
          </div>
          <div class="ds-swatch" style="background: var(--text-secondary)">
            <span class="ds-swatch-name" style="color: var(--bg-primary)">--text-secondary</span>
          </div>
          <div class="ds-swatch" style="background: var(--text-tertiary)">
            <span class="ds-swatch-name" style="color: var(--bg-primary)">--text-tertiary</span>
          </div>
          <div class="ds-swatch" style="background: var(--text-disabled)">
            <span class="ds-swatch-name" style="color: var(--bg-primary)">--text-disabled</span>
          </div>
        </div>
      </div>

      <div class="ds-group">
        <h3 class="ds-group-title">Game-Specific Colors</h3>
        <div class="ds-swatches">
          <div class="ds-swatch" style="background: var(--roulette-red)">
            <span class="ds-swatch-name">--roulette-red</span>
          </div>
          <div class="ds-swatch" style="background: var(--roulette-red-dark)">
            <span class="ds-swatch-name">--roulette-red-dark</span>
          </div>
          <div class="ds-swatch" style="background: var(--roulette-green)">
            <span class="ds-swatch-name">--roulette-green</span>
          </div>
          <div class="ds-swatch" style="background: var(--roulette-green-dark)">
            <span class="ds-swatch-name">--roulette-green-dark</span>
          </div>
          <div class="ds-swatch" style="background: var(--roulette-voisins)">
            <span class="ds-swatch-name">--roulette-voisins</span>
          </div>
          <div class="ds-swatch" style="background: var(--roulette-tiers)">
            <span class="ds-swatch-name">--roulette-tiers</span>
          </div>
          <div class="ds-swatch" style="background: var(--roulette-orphelins)">
            <span class="ds-swatch-name">--roulette-orphelins</span>
          </div>
        </div>
      </div>

      <div class="ds-group">
        <h3 class="ds-group-title">Gradients</h3>
        <div class="ds-swatches">
          <div class="ds-swatch" style="background: var(--gradient-avatar)">
            <span class="ds-swatch-name">--gradient-avatar</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-accent)">
            <span class="ds-swatch-name">--gradient-accent</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt1)">
            <span class="ds-swatch-name">--gradient-gt1</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt2)">
            <span class="ds-swatch-name">--gradient-gt2</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt3)">
            <span class="ds-swatch-name">--gradient-gt3</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt4)">
            <span class="ds-swatch-name">--gradient-gt4</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt5)">
            <span class="ds-swatch-name">--gradient-gt5</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt6)">
            <span class="ds-swatch-name">--gradient-gt6</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt7)">
            <span class="ds-swatch-name">--gradient-gt7</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt8)">
            <span class="ds-swatch-name">--gradient-gt8</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt9)">
            <span class="ds-swatch-name">--gradient-gt9</span>
          </div>
          <div class="ds-swatch" style="background: var(--gradient-gt10)">
            <span class="ds-swatch-name">--gradient-gt10</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Typography -->
    <section class="ds-section">
      <h2 class="ds-section-title">Typography</h2>

      <div class="ds-group">
        <h3 class="ds-group-title">Font Families</h3>
        <div class="ds-type-specimens">
          <div class="ds-type-row">
            <span class="ds-type-label">--font-family-primary</span>
            <span class="ds-type-sample ds-font-primary">The quick brown fox jumps over the lazy dog — 0123456789</span>
          </div>
          <div class="ds-type-row">
            <span class="ds-type-label">--font-family-display</span>
            <span class="ds-type-sample ds-font-display">Casino Royal Jackpot 777</span>
          </div>
          <div class="ds-type-row">
            <span class="ds-type-label">--font-family-mono</span>
            <span class="ds-type-sample ds-font-mono">$1,234.56 | 0xA4B2C3 | 99.97% RTP</span>
          </div>
        </div>
      </div>

      <div class="ds-group">
        <h3 class="ds-group-title">Font Sizes</h3>
        <div class="ds-type-scale">
          <div class="ds-size-row" style="font-size: var(--font-size-display); font-weight: var(--font-weight-bold)">Display (2rem)</div>
          <div class="ds-size-row" style="font-size: var(--font-size-h1); font-weight: var(--font-weight-semibold)">Heading H1 (1.5rem)</div>
          <div class="ds-size-row" style="font-size: var(--font-size-h2); font-weight: var(--font-weight-semibold)">Heading H2 (1.125rem)</div>
          <div class="ds-size-row" style="font-size: var(--font-size-body)">Body (0.875rem)</div>
          <div class="ds-size-row" style="font-size: var(--font-size-sm)">Small (0.75rem)</div>
          <div class="ds-size-row" style="font-size: var(--font-size-caption)">Caption (0.625rem)</div>
        </div>
      </div>

      <div class="ds-group">
        <h3 class="ds-group-title">Font Weights</h3>
        <div class="ds-weight-scale">
          <div class="ds-weight-row" style="font-weight: var(--font-weight-regular)">Regular (400) — The quick brown fox</div>
          <div class="ds-weight-row" style="font-weight: var(--font-weight-medium)">Medium (500) — The quick brown fox</div>
          <div class="ds-weight-row" style="font-weight: var(--font-weight-semibold)">Semibold (600) — The quick brown fox</div>
          <div class="ds-weight-row" style="font-weight: var(--font-weight-bold)">Bold (700) — The quick brown fox</div>
        </div>
      </div>
    </section>

    <!-- Spacing -->
    <section class="ds-section">
      <h2 class="ds-section-title">Spacing</h2>
      <div class="ds-group">
        <div class="ds-spacing-scale">
          <div v-for="(size, token) in spacingTokens" :key="token" class="ds-spacing-row">
            <span class="ds-spacing-label">{{ token }}</span>
            <div class="ds-spacing-bar" :style="{ width: size, backgroundColor: 'var(--accent-primary)' }"></div>
            <span class="ds-spacing-value">{{ size }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Border Radius -->
    <section class="ds-section">
      <h2 class="ds-section-title">Border Radius</h2>
      <div class="ds-group">
        <div class="ds-radius-scale">
          <div class="ds-radius-row">
            <div class="ds-radius-box" style="border-radius: var(--radius-sm)"></div>
            <span>--radius-sm (4px)</span>
          </div>
          <div class="ds-radius-row">
            <div class="ds-radius-box" style="border-radius: var(--radius-md)"></div>
            <span>--radius-md (8px)</span>
          </div>
          <div class="ds-radius-row">
            <div class="ds-radius-box" style="border-radius: var(--radius-lg)"></div>
            <span>--radius-lg (12px)</span>
          </div>
          <div class="ds-radius-row">
            <div class="ds-radius-box" style="border-radius: var(--radius-xl)"></div>
            <span>--radius-xl (16px)</span>
          </div>
          <div class="ds-radius-row">
            <div class="ds-radius-box ds-radius-pill" style="border-radius: var(--radius-full)"></div>
            <span>--radius-full (9999px)</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Shadows -->
    <section class="ds-section">
      <h2 class="ds-section-title">Shadows</h2>
      <div class="ds-group">
        <div class="ds-shadow-scale">
          <div class="ds-shadow-box" style="box-shadow: var(--shadow-sm)">shadow-sm</div>
          <div class="ds-shadow-box" style="box-shadow: var(--shadow-md)">shadow-md</div>
          <div class="ds-shadow-box" style="box-shadow: var(--shadow-lg)">shadow-lg</div>
          <div class="ds-shadow-box" style="box-shadow: var(--shadow-glow-green)">glow-green</div>
          <div class="ds-shadow-box" style="box-shadow: var(--shadow-glow-gold)">glow-gold</div>
          <div class="ds-shadow-box" style="box-shadow: var(--shadow-glow-red)">glow-red</div>
        </div>
      </div>
    </section>

    <!-- Transitions -->
    <section class="ds-section">
      <h2 class="ds-section-title">Transitions</h2>
      <div class="ds-group">
        <div class="ds-transition-scale">
          <div class="ds-transition-box" @mouseenter="hovered = 'fast'" @mouseleave="hovered = null"
            :class="{ 'ds-transition-active': hovered === 'fast' }"
            style="transition: background var(--transition-fast)">
            transition-fast (150ms)
          </div>
          <div class="ds-transition-box" @mouseenter="hovered = 'normal'" @mouseleave="hovered = null"
            :class="{ 'ds-transition-active': hovered === 'normal' }"
            style="transition: background var(--transition-normal)">
            transition-normal (250ms)
          </div>
          <div class="ds-transition-box" @mouseenter="hovered = 'slow'" @mouseleave="hovered = null"
            :class="{ 'ds-transition-active': hovered === 'slow' }"
            style="transition: background var(--transition-slow)">
            transition-slow (400ms)
          </div>
        </div>
      </div>
    </section>

    <!-- Z-Index -->
    <section class="ds-section">
      <h2 class="ds-section-title">Z-Index Scale</h2>
      <div class="ds-group">
        <div class="ds-token-list">
          <div class="ds-token-row"><code>--z-base</code> <span class="ds-token-val">0</span></div>
          <div class="ds-token-row"><code>--z-dropdown</code> <span class="ds-token-val">100</span></div>
          <div class="ds-token-row"><code>--z-sticky</code> <span class="ds-token-val">200</span></div>
          <div class="ds-token-row"><code>--z-sidebar</code> <span class="ds-token-val">300</span></div>
          <div class="ds-token-row"><code>--z-modal</code> <span class="ds-token-val">400</span></div>
          <div class="ds-token-row"><code>--z-notification</code> <span class="ds-token-val">500</span></div>
          <div class="ds-token-row"><code>--z-overlay</code> <span class="ds-token-val">600</span></div>
        </div>
      </div>
    </section>

    <!-- Layout -->
    <section class="ds-section">
      <h2 class="ds-section-title">Layout Tokens</h2>
      <div class="ds-group">
        <div class="ds-token-list">
          <div class="ds-token-row"><code>--sidebar-width</code> <span class="ds-token-val">240px</span></div>
          <div class="ds-token-row"><code>--sidebar-collapsed</code> <span class="ds-token-val">64px</span></div>
          <div class="ds-token-row"><code>--header-height</code> <span class="ds-token-val">60px</span></div>
          <div class="ds-token-row"><code>--bottom-nav-height</code> <span class="ds-token-val">56px</span></div>
          <div class="ds-token-row"><code>--content-max-width</code> <span class="ds-token-val">1200px</span></div>
          <div class="ds-token-row"><code>--admin-sidebar-width</code> <span class="ds-token-val">260px</span></div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const hovered = ref<string | null>(null)

const spacingTokens: Record<string, string> = {
  '--space-1': '4px',
  '--space-2': '8px',
  '--space-3': '12px',
  '--space-4': '16px',
  '--space-5': '20px',
  '--space-6': '24px',
  '--space-8': '32px',
  '--space-10': '40px',
  '--space-12': '48px',
}
</script>

<style scoped>
.ds-page {
  padding: var(--space-6, 24px);
  max-width: 1100px;
  margin: 0 auto;
  color: var(--text-secondary, #b1bad3);
}

.ds-header {
  margin-bottom: var(--space-8, 32px);
}

.ds-title {
  font-size: var(--font-size-display, 2rem);
  font-weight: var(--font-weight-bold, 700);
  color: var(--text-primary, #fff);
  font-family: var(--font-family-display, 'Russo One', sans-serif);
}

.ds-subtitle {
  font-size: var(--font-size-body, 0.875rem);
  color: var(--text-tertiary, #7f8fa4);
  margin-top: var(--space-1, 4px);
}

.ds-section {
  margin-bottom: var(--space-12, 48px);
}

.ds-section-title {
  font-size: var(--font-size-h2, 1.125rem);
  font-weight: var(--font-weight-bold, 700);
  color: var(--accent-primary, #00e701);
  border-bottom: 1px solid var(--bg-tertiary, #213743);
  padding-bottom: var(--space-2, 8px);
  margin-bottom: var(--space-6, 24px);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.ds-group {
  margin-bottom: var(--space-6, 24px);
}

.ds-group-title {
  font-size: var(--font-size-sm, 0.75rem);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: var(--space-3, 12px);
}

/* Color Swatches */
.ds-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3, 12px);
}

.ds-swatch {
  width: 120px;
  height: 80px;
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: flex-end;
  padding: var(--space-2, 8px);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.3));
  border: 1px solid rgba(255,255,255,0.06);
}

.ds-swatch-name {
  font-size: 9px;
  font-family: var(--font-family-mono, monospace);
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  background: rgba(0,0,0,0.4);
  padding: 2px 4px;
  border-radius: 3px;
  word-break: break-all;
}

/* Typography */
.ds-type-specimens {
  display: flex;
  flex-direction: column;
  gap: var(--space-4, 16px);
}

.ds-type-row {
  display: flex;
  align-items: baseline;
  gap: var(--space-4, 16px);
}

.ds-type-label {
  font-size: var(--font-size-sm, 0.75rem);
  color: var(--text-tertiary, #7f8fa4);
  font-family: var(--font-family-mono, monospace);
  flex-shrink: 0;
  width: 200px;
}

.ds-type-sample {
  font-size: 18px;
  color: var(--text-primary, #fff);
}

.ds-font-primary { font-family: var(--font-family-primary, 'Inter', sans-serif); }
.ds-font-display { font-family: var(--font-family-display, 'Russo One', sans-serif); }
.ds-font-mono    { font-family: var(--font-family-mono, 'JetBrains Mono', monospace); }

.ds-type-scale {
  display: flex;
  flex-direction: column;
  gap: var(--space-3, 12px);
}

.ds-size-row {
  color: var(--text-primary, #fff);
}

.ds-weight-scale {
  display: flex;
  flex-direction: column;
  gap: var(--space-3, 12px);
}

.ds-weight-row {
  font-size: 18px;
  color: var(--text-primary, #fff);
}

/* Spacing */
.ds-spacing-scale {
  display: flex;
  flex-direction: column;
  gap: var(--space-2, 8px);
}

.ds-spacing-row {
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
}

.ds-spacing-label {
  font-size: var(--font-size-sm, 0.75rem);
  font-family: var(--font-family-mono, monospace);
  color: var(--text-tertiary, #7f8fa4);
  width: 100px;
  flex-shrink: 0;
}

.ds-spacing-bar {
  height: 12px;
  border-radius: var(--radius-sm, 4px);
  background: var(--accent-primary, #00e701);
}

.ds-spacing-value {
  font-size: var(--font-size-sm, 0.75rem);
  font-family: var(--font-family-mono, monospace);
  color: var(--text-secondary, #b1bad3);
}

/* Border Radius */
.ds-radius-scale {
  display: flex;
  gap: var(--space-6, 24px);
  flex-wrap: wrap;
  align-items: flex-end;
}

.ds-radius-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2, 8px);
  font-size: var(--font-size-sm, 0.75rem);
  color: var(--text-tertiary, #7f8fa4);
  font-family: var(--font-family-mono, monospace);
  text-align: center;
}

.ds-radius-box {
  width: 60px;
  height: 40px;
  background: var(--bg-tertiary, #213743);
  border: 2px solid var(--accent-primary, #00e701);
}

.ds-radius-pill {
  width: 80px;
  height: 30px;
}

/* Shadows */
.ds-shadow-scale {
  display: flex;
  gap: var(--space-6, 24px);
  flex-wrap: wrap;
}

.ds-shadow-box {
  width: 120px;
  height: 70px;
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--font-size-sm, 0.75rem);
  color: var(--text-secondary, #b1bad3);
  font-family: var(--font-family-mono, monospace);
}

/* Transitions */
.ds-transition-scale {
  display: flex;
  gap: var(--space-4, 16px);
  flex-wrap: wrap;
}

.ds-transition-box {
  padding: var(--space-4, 16px) var(--space-6, 24px);
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-md, 8px);
  font-size: var(--font-size-sm, 0.75rem);
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  border: 1px solid var(--bg-tertiary, #213743);
}

.ds-transition-active {
  background: var(--bg-hover, #2a4455) !important;
  color: var(--text-primary, #fff) !important;
}

/* Z-Index / Layout Tokens */
.ds-token-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2, 8px);
}

.ds-token-row {
  display: flex;
  align-items: center;
  gap: var(--space-4, 16px);
  padding: var(--space-2, 8px) var(--space-3, 12px);
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-sm, 4px);
}

.ds-token-row code {
  font-family: var(--font-family-mono, monospace);
  font-size: var(--font-size-sm, 0.75rem);
  color: var(--accent-secondary, #1475e1);
}

.ds-token-val {
  font-family: var(--font-family-mono, monospace);
  font-size: var(--font-size-sm, 0.75rem);
  color: var(--text-tertiary, #7f8fa4);
  margin-left: auto;
}
</style>
